<div class="space-y-3 sm:space-y-6 pb-16 sm:pb-8">
  <!-- Top Header & Tactical Controls (Material Card) -->
  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4 bg-white dark:bg-slate-900 p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs dark:shadow-md transition-colors">
    <div>
      <div class="flex items-center space-x-2">
        <h2 class="text-xl font-bold text-slate-900 dark:text-white">{{ game.t('squad.title') }}</h2>
        <span class="px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
          {{ game.t('squad.badge_management') }}
        </span>
      </div>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        {{ game.t('squad.desc') }}
      </p>
    </div>

    <!-- Formation Selector & Auto Button -->
    <div class="flex flex-wrap items-center gap-2">
      <!-- Botón Selector de Formación Táctica (Abre Modal Dialog) -->
      <button
        type="button"
        (click)="openFormationModal()"
        [disabled]="isLocked()"
        class="flex items-center space-x-1.5 px-3 py-2 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 text-xs font-bold rounded-xl border border-emerald-500/20 transition-colors cursor-pointer disabled:opacity-50"
        [title]="game.t('squad.formation')"
        [attr.aria-label]="game.t('squad.formation') + ': ' + game.activeFormation()"
      >
        <span class="text-slate-500 dark:text-slate-400 font-medium">{{ game.t('squad.formation') }}:</span>
        <span class="font-mono">
          {{ game.activeFormation() }}
        </span>
        <app-icon name="chevron-down" [size]="14" />
      </button>

      <button
        (click)="game.autoPickLineup()"
        [disabled]="!game.canAction('saveLineup') || (isLocked())"
        class="flex items-center space-x-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl border border-slate-200 dark:border-slate-700 transition-colors disabled:opacity-50 cursor-pointer"
        [title]="game.t('squad.auto_pick')"
      >
        <app-icon name="sparkles" [size]="14" class="text-amber-500" />
        <span>{{ game.t('squad.auto_pick') }}</span>
      </button>

      <!-- Botón Guardar Alineación con validación 11/11 -->
      <button
        (click)="saveLineup()"
        [disabled]="!game.canAction('saveLineup') || (isLocked() || isSaving())"
        class="flex items-center space-x-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl shadow-xs transition-all cursor-pointer active:scale-98 disabled:opacity-50"
        [ngClass]="assignedCount() === 11 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'"
        [title]="'Guardar alineación y banquillo'"
      >
        @if (isSaving()) {
          <app-icon name="loader" [size]="14" class="animate-spin" />
          <span>{{ game.t('squad.saving') }}</span>
        } @else if (saveStatus() === 'saved') {
          <app-icon name="check-circle" [size]="14" class="text-emerald-300" />
          <span>{{ game.t('squad.saved') }}</span>
        } @else {
          <app-icon name="check-circle" [size]="14" />
          <span>Guardar alineación y banquillo</span>
        }
      </button>

      @if (isLocked()) {
        <div class="flex items-center space-x-1 px-3 py-1.5 bg-amber-50 dark:bg-amber-950/80 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/80 rounded-xl text-xs font-bold">
          <app-icon name="lock" [size]="14" />
          <span>{{ game.t('squad.locked_xi') }}</span>
        </div>
      }
    </div>
  </div>

  <!-- Banner de error al guardar alineación -->
  @if (saveErrorMessage()) {
    <div
      class="p-3.5 rounded-xl border text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs animate-in fade-in transition-all bg-rose-50 dark:bg-rose-950/60 border-rose-200 dark:border-rose-800/80 text-rose-800 dark:text-rose-300"
    >
      <div class="flex items-center space-x-2">
        <app-icon name="alert-triangle" [size]="16" class="shrink-0" />
        <span class="font-medium">{{ saveErrorMessage() }}</span>
      </div>
      <button
        type="button"
        (click)="saveErrorMessage.set(null)"
        class="px-2 py-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs font-medium cursor-pointer"
      >
        Cerrar
      </button>
    </div>
  }

  <!-- Main Layout: 2 Columns (Tactical Pitch on Left, Roster Table on Right) -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-6">
    <!-- Tactical Board / Soccer Pitch (5 cols on lg) -->
    <div class="lg:col-span-5 bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 p-2.5 sm:p-5 flex flex-col justify-between shadow-xs transition-colors">
      @let metrics = starterMetrics();
      <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 mb-3 text-xs">
        <span class="text-slate-500 dark:text-slate-400 uppercase font-bold whitespace-nowrap">
          {{ game.t('squad.tactical_board') }} ({{ game.activeFormation() }})
        </span>
        <div class="flex items-center justify-center gap-2 whitespace-nowrap" aria-label="Totales del once alineado">
          <span class="inline-flex items-center gap-1" title="Valor de los jugadores alineados">
            <app-icon name="coins" [size]="14" class="text-slate-500 dark:text-slate-400" aria-hidden="true" />
            <span class="sr-only">Valor del once:</span>
            <strong class="font-mono text-slate-700 dark:text-slate-300">{{ formatMarketValue(metrics.value) }}</strong>
          </span>
          <span class="inline-flex items-center gap-1" title="Suma de las medias globales de los jugadores alineados">
            <app-icon name="star" [size]="14" class="text-amber-500" aria-hidden="true" />
            <span class="sr-only">Suma de medias globales del once:</span>
            <strong class="font-mono text-amber-600 dark:text-amber-400">{{ formatPoints(metrics.global) }}</strong>
          </span>
          <span class="inline-flex items-center gap-1" title="Suma de las medias de los últimos tres partidos de los jugadores alineados">
            <app-icon name="trending-up" [size]="14" class="text-emerald-500" aria-hidden="true" />
            <span class="sr-only">Suma de forma del once:</span>
            <strong class="font-mono text-emerald-600 dark:text-emerald-400">{{ formatPoints(metrics.form) }}</strong>
          </span>
        </div>
      </div>


      <!-- Soccer Pitch Graphic -->
      <div class="relative w-full aspect-[3/4] bg-emerald-950/90 dark:bg-[#0c131a] rounded-xl sm:rounded-2xl border border-emerald-800/40 dark:border-white/10 overflow-hidden flex flex-col justify-between p-1.5 sm:p-3 select-none shadow-inner">
        <!-- Pitch lines -->
        <div class="absolute inset-2 border border-white/20 rounded-xl pointer-events-none"></div>
        <!-- Center line -->
        <div class="absolute top-1/2 left-2 right-2 border-b border-white/20 pointer-events-none"></div>
        <!-- Center circle -->
        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 border border-white/20 rounded-full pointer-events-none"></div>
        <!-- Top penalty area (opponent goal) -->
        <div class="absolute top-2 left-1/2 -translate-x-1/2 w-36 h-16 border-b border-x border-white/20 rounded-b-lg pointer-events-none"></div>
        <!-- Bottom penalty area (user goal) -->
        <div class="absolute bottom-2 left-1/2 -translate-x-1/2 w-36 h-16 border-t border-x border-white/20 rounded-t-lg pointer-events-none"></div>

        <!-- Player Slots on Pitch -->
        @for (slot of game.lineupSlots(); track slot.slotId) {
          <div
            class="absolute -translate-x-1/2 -translate-y-1/2 transition-all cursor-pointer z-10"
            [style.left.%]="slot.x"
            [style.top.%]="slot.y"
            (click)="handleSlotClick(slot)"
          >
            @if (getPlayerInSlot(slot.playerId); as player) {
              <div
                class="group flex flex-col items-center"
                [title]="player.name + ' (' + getPlayerClubName(player) + ' · ' + player.position + ' · Media: ' + formatPoints(player.averagePoints) + ' · Valor: ' + formatMarketValue(player.currentMarketValue) + (hasUnavailability(player) ? ' · ⚠️ ' + (getPrimaryUnavailability(player)?.typeLabel || getUnavailabilityTypeLabel(getPrimaryUnavailability(player)?.type)) + ': ' + (getPrimaryUnavailability(player)?.reasonLabel || getUnavailabilityReasonLabel(getPrimaryUnavailability(player)?.reason)) : '') + ')'"
              >
                <!-- Avatar circle with real club crest & injury badge -->
                <div class="relative">
                  <div
                    class="w-11 h-11 rounded-full flex flex-col items-center justify-center font-bold text-xs text-white border transition-transform group-hover:scale-105 shadow-md"
                    [style.backgroundColor]="'#0f172a'"
                    [style.borderColor]="slot.slotId === selectedSlotId ? '#38bdf8' : hasUnavailability(player) ? '#f43f5e' : 'rgba(255, 255, 255, 0.35)'"
                  >
                    <span class="text-[9px] font-black text-emerald-400 tracking-wider leading-none">{{ player.position }}</span>
                    @if (player.averagePoints !== null && player.averagePoints !== undefined) {
                      <span class="text-[8px] font-mono text-amber-300 font-bold leading-tight">{{ player.averagePoints.toFixed(1) }}</span>
                    }
                  </div>

                  <!-- Escudo pequeño del club real -->
                  @if (getPlayerCrestUrl(player); as crestUrl) {
                    <img
                      [src]="crestUrl"
                      [alt]="getPlayerClubShortName(player) || 'Club'"
                      (error)="onCrestError($event)"
                      referrerpolicy="no-referrer"
                      class="absolute -bottom-1 -right-1 w-5 h-5 rounded-full object-contain bg-white dark:bg-slate-900 p-0.5 border border-white/70 dark:border-slate-700 shadow-xs"
                      [title]="getPlayerClubName(player)"
                    />
                  }

                  <!-- Indicador si está lesionado o sancionado -->
                  @if (hasUnavailability(player)) {
                    @let unav = getPrimaryUnavailability(player);
                    <div
                      class="absolute -top-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center border border-white dark:border-slate-900 shadow-xs animate-pulse"
                      [ngClass]="getUnavailabilityBadgeClass(unav?.type)"
                      [title]="(unav?.typeLabel || getUnavailabilityTypeLabel(unav?.type)) + ': ' + (unav?.reasonLabel || getUnavailabilityReasonLabel(unav?.reason)) + (unav?.expectedEndDate ? ' (hasta ' + unav?.expectedEndDate + ')' : '')"
                    >
                      <app-icon [name]="getUnavailabilityIcon(unav?.type)" [size]="9" />
                    </div>
                  }
                </div>

                <!-- Nombre del jugador y aviso de baja -->
                <div class="mt-1 flex flex-col items-center">
                  <span
                    class="px-1.5 py-0.5 rounded text-[10px] font-medium max-w-[76px] truncate border shadow-xs"
                    [ngClass]="hasUnavailability(player) ? 'bg-rose-950/90 text-rose-200 border-rose-700/60' : 'bg-slate-900/90 text-slate-100 border-white/10'"
                  >
                    {{ player.name.split(' ').pop() }}
                  </span>
                  @if (hasUnavailability(player)) {
                    <span class="text-[8px] font-bold text-rose-400 leading-tight">
                      {{ getPrimaryUnavailability(player)?.typeLabel || 'Baja' }}
                    </span>
                  } @else {
                    <span class="text-[9px] font-bold text-emerald-300">
                      {{ slot.label }}
                    </span>
                  }
                </div>
              </div>
            } @else {
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 rounded-full border border-dashed flex items-center justify-center text-xs font-semibold transition-all hover:scale-105"
                  [ngClass]="slot.slotId === selectedSlotId ? 'border-sky-400 bg-sky-950/60 text-sky-300' : 'border-emerald-700/60 bg-emerald-900/40 text-emerald-200 hover:border-emerald-400 hover:text-white'"
                >
                  {{ slot.label }}
                </div>
                <span class="text-[9px] font-medium text-emerald-300/70 mt-1">Vacío</span>
              </div>
            }
          </div>
        }
      </div>

      <p class="text-[11px] text-slate-500 dark:text-slate-400 text-center mt-3">
        Toca una posición sobre el campo para asignar o sustituir al jugador titular.
      </p>

      <!-- Banquillo Habitual (0-5 suplentes) -->
      <div class="mt-4 pt-3.5 border-t border-slate-200 dark:border-slate-800">
        <div class="flex items-center justify-between mb-2.5">
          <div class="flex items-center space-x-1.5">
            <app-icon name="users" [size]="15" class="text-emerald-500" />
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
              Banquillo
            </h4>
            <span class="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
              {{ benchCount() }}/5
            </span>
          </div>
          @if (benchCount() < 5 && availableBenchSubstitutes().length > 0) {
            <button
              type="button"
              (click)="openBenchPicker()"
              class="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 transition-colors flex items-center space-x-1 cursor-pointer"
            >
              <app-icon name="plus" [size]="12" />
              <span>Añadir suplente</span>
            </button>
          }
        </div>

        <!-- 5 Slots de banquillo -->
        <div class="grid grid-cols-5 gap-1.5 sm:gap-2">
          @for (slotIndex of [0, 1, 2, 3, 4]; track slotIndex) {
            @let subPlayer = getBenchPlayerAt(slotIndex);
            @if (subPlayer) {
              <div class="relative group p-1.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-800/60 flex flex-col items-center text-center">
                <button
                  type="button"
                  (click)="removeBenchSubstitute(subPlayer.id || subPlayer.playerInstanceId)"
                  class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center text-[10px] opacity-80 hover:opacity-100 cursor-pointer shadow-xs z-10"
                  title="Quitar suplente"
                >
                  <app-icon name="x" [size]="10" />
                </button>
                <div class="w-8 h-8 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 mb-1 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300">
                  {{ subPlayer.name?.charAt(0) || '?' }}
                </div>
                <span class="text-[9px] font-bold text-slate-800 dark:text-slate-200 truncate w-full" [title]="subPlayer.name">
                  {{ subPlayer.name.split(' ').pop() }}
                </span>
                <span class="text-[8px] font-mono font-semibold px-1 rounded bg-slate-200/80 dark:bg-slate-700 text-slate-600 dark:text-slate-300 mt-0.5">
                  {{ subPlayer.position }}
                </span>
              </div>
            } @else {
              <button
                type="button"
                (click)="openBenchPicker()"
                class="p-2 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 hover:border-emerald-500 hover:text-emerald-500 dark:hover:border-emerald-400 dark:hover:text-emerald-300 transition-colors cursor-pointer min-h-[64px]"
                title="Añadir suplente al banquillo"
              >
                <app-icon name="plus" [size]="13" />
                <span class="text-[8px] font-medium mt-0.5">Vacío</span>
              </button>
            }
          }
        </div>
      </div>
      <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
        @if (game.coaches.isLoadingContracts()) {
          <p class="text-xs text-slate-500 dark:text-slate-400">Cargando entrenador...</p>
        } @else if (game.coaches.contractsError()) {
          <p class="text-xs text-rose-600 dark:text-rose-400">{{ game.coaches.contractsError() }}</p>
          <button type="button" (click)="refreshCoach()" class="mt-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400 cursor-pointer">Reintentar</button>
        } @else if (game.coaches.activeContract(); as contract) {
              <div class="space-y-4">
                <!-- Left: Coach Profile -->
                <div class="flex items-center space-x-2.5">
                  <div class="relative w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-0.5 shrink-0 shadow-2xs">
                    @if (contract.realClub?.shortName; as shortName) {
                      <img
                        [src]="'assets/crest/' + shortName.trim().toUpperCase() + '.png'"
                        [alt]="contract.realClub?.name || shortName"
                        (error)="onCrestError($event)"
                        class="w-full h-full object-contain"
                        referrerpolicy="no-referrer"
                      />
                    } @else {
                      <div class="w-full h-full flex items-center justify-center text-slate-400">
                        <app-icon name="shield" [size]="16" />
                      </div>
                    }
                  </div>
                  <div>
                    <h4 class="text-sm font-bold text-slate-900 dark:text-white">{{ contract.name }}</h4>
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                      Entrenador Principal · {{ game.userClub().name }}
                    </p>
                    <div class="mt-2 text-[11px] text-slate-500">
                      Firmado: <strong>{{ (contract.signed_at | date:'dd/MM/yyyy HH:mm') }}</strong>
                    </div>
                  </div>
                </div>

                <!-- Center: Contract Conditions -->
                <div class="grid grid-cols-3 gap-2 p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 text-center" aria-label="Mejoras del entrenador">
                  <div>
                    <div class="text-[10px] uppercase font-bold text-slate-400">{{ game.t('coaches.defense') }}</div>
                    <div class="text-sm font-bold text-amber-600 dark:text-amber-400 font-mono">
                      @if (contract.defense != null) { +{{ contract.defense | number:'1.0-2' }} % } @else { — }
                    </div>
                  </div>
                  <div class="border-x border-slate-200 dark:border-slate-800">
                    <div class="text-[10px] uppercase font-bold text-slate-400">{{ game.t('coaches.midfield') }}</div>
                    <div class="text-sm font-bold text-blue-600 dark:text-blue-400 font-mono">
                      @if (contract.midfield != null) { +{{ contract.midfield | number:'1.0-2' }} % } @else { — }
                    </div>
                  </div>
                  <div>
                    <div class="text-[10px] uppercase font-bold text-slate-400">{{ game.t('coaches.attack') }}</div>
                    <div class="text-sm font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                      @if (contract.attack != null) { +{{ contract.attack | number:'1.0-2' }} % } @else { — }
                    </div>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">

                  <div class="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div class="text-[10px] uppercase font-bold text-slate-400">Salario Anual</div>
                    <div class="text-sm font-bold text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
                      {{ contract.annual_salary | number:'1.0-0' }} €
                    </div>
                    <div class="text-[10px] text-slate-400 mt-1">Prorrateado por jornada</div>
                  </div>

                  <div class="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div class="text-[10px] uppercase font-bold text-slate-400">Cláusula Rescisión</div>
                    <div class="text-sm font-bold text-slate-900 dark:text-white font-mono mt-0.5">
                      {{ contract.release_clause | number:'1.0-0' }} €
                    </div>
                    <div class="text-[10px] text-slate-400 mt-1">Blindaje informativo</div>
                  </div>
                  
                </div>
              </div>
             
        } @else {
          <p class="text-xs text-slate-500 dark:text-slate-400">{{ game.t('coaches.no_active_coach') }}</p>
          <a
            routerLink="/market"
            [queryParams]="{ tab: 'coaches' }"
            class="mt-3 inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
          >
            <app-icon name="store" [size]="15" class="shrink-0" />
            <span>{{ game.t('coaches.go_to_market') }}</span>
          </a>
        }
      </div>
    </div>

    <!-- Squad Roster Section (7 cols on lg) -->
    <div class="lg:col-span-7 bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 p-2 sm:p-5 shadow-xs space-y-2.5 sm:space-y-4 transition-colors">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
        <div class="flex items-center space-x-2">
          <h3 class="text-sm sm:text-base font-bold text-slate-900 dark:text-white">Plantilla</h3>
          <span class="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-full font-medium">
            {{ game.userSquad().length }} futbolistas
          </span>
          @if (game.isSquadLoading()) {
            <span class="inline-flex items-center space-x-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              <app-icon name="loader" [size]="12" class="animate-spin" />
              <span>Sincronizando...</span>
            </span>
          }
        </div>

        <!-- Controls: View Mode Switcher, Position Filters and Refresh -->
        <div class="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs">
          <!-- View Switcher (Tarjetas / Tabla) -->
          <div class="flex items-center bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700">
            <button
              type="button"
              (click)="viewMode.set('cards')"
              class="flex items-center space-x-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md font-semibold transition-all cursor-pointer text-[11px] sm:text-xs"
              [ngClass]="viewMode() === 'cards' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white'"
              title="Vista en tarjetas"
            >
              <app-icon name="grid" [size]="12" />
              <span>Tarjetas</span>
            </button>
            <button
              type="button"
              (click)="viewMode.set('table')"
              class="flex items-center space-x-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md font-semibold transition-all cursor-pointer text-[11px] sm:text-xs"
              [ngClass]="viewMode() === 'table' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white'"
              title="Vista en tabla"
            >
              <app-icon name="list" [size]="12" />
              <span>Tabla</span>
            </button>
          </div>

          <!-- Position Filter Pills -->
          <div class="flex items-center space-x-1">
            @for (pos of ['ALL', 'POR', 'DEF', 'MED', 'DEL']; track pos) {
              <button
                (click)="filterPos = pos"
                class="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg font-semibold transition-colors cursor-pointer text-[11px] sm:text-xs"
                [ngClass]="filterPos === pos ? 'bg-emerald-600 text-white shadow-xs' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-white'"
              >
                {{ pos === 'ALL' ? game.t('common.all') : pos === 'POR' ? game.t('squad.pos_gk') : pos === 'DEF' ? game.t('squad.pos_df') : pos === 'MED' ? game.t('squad.pos_mf') : game.t('squad.pos_fw') }}
              </button>
            }
          </div>
        </div>
      </div>

      <ng-container *ngTemplateOutlet="squadPlayers; context: { $implicit: professionalSquad() }" />

      @if (amateurSquad().length > 0) {
        <details class="group/amateurs mt-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
          <summary class="flex items-center justify-between gap-3 p-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-sm font-semibold text-slate-700 dark:text-slate-200">
            <span class="flex items-center gap-2">
              <app-icon name="users" [size]="16" class="text-slate-400" />
              <span>Amateurs</span>
              <span class="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-xs font-mono">{{ amateurSquad().length }}</span>
            </span>
            <app-icon name="chevron-down" [size]="16" class="transition-transform group-open/amateurs:rotate-180" />
          </summary>
          <div class="p-2 sm:p-3 pt-0">
            <ng-container *ngTemplateOutlet="squadPlayers; context: { $implicit: amateurSquad() }" />
          </div>
        </details>
      }

      <ng-template #squadPlayers let-players>
      <!-- VISTA 1: GRID DE TARJETAS (Moderna, visual y con métricas) -->
      @if (viewMode() === 'cards') {
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
          @for (player of players; track player.id) {
            <div
              class="p-2.5 sm:p-4 rounded-xl border transition-all duration-200 flex flex-col justify-between space-y-2 sm:space-y-3 bg-white dark:bg-slate-900 shadow-xs hover:shadow-md"
              [ngClass]="isPlayerStarter(player.id)
                ? 'border-emerald-500/50 bg-emerald-50/20 dark:bg-emerald-950/20 ring-1 ring-emerald-500/20'
                : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'"
            >
              <!-- Card Header: Crest + Name + Position + Starter Pill -->
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-center space-x-2.5">
                  <!-- Escudo del Club Real -->
                  <div class="relative w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-0.5 shrink-0 shadow-2xs">
                    @if (getPlayerCrestUrl(player); as crestUrl) {
                      <img
                        [src]="crestUrl"
                        [alt]="getPlayerClubShortName(player) || 'Club'"
                        (error)="onCrestError($event)"
                        referrerpolicy="no-referrer"
                        class="w-full h-full object-contain"
                        [title]="getPlayerClubName(player)"
                      />
                    } @else {
                      <span class="text-[9px] font-mono font-bold text-slate-500 uppercase">
                        {{ getPlayerClubShortName(player) || 'CLB' }}
                      </span>
                    }
                  </div>

                  <div>
                    <div class="flex items-center gap-1.5 flex-wrap">
                      @if (player.isAmateur) {
                        <span class="font-bold text-sm text-slate-900 dark:text-white">{{ player.name }}</span>
                      } @else {
                      <button
                        type="button"
                        (click)="openPlayerDetail(player)"
                        class="font-bold text-sm text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer text-left focus:outline-none"
                      >
                        {{ player.name }}
                      </button>
                      }
                      <span class="inline-flex items-center gap-1.5">
                        <span class="px-1.5 py-0.2 rounded text-[10px] font-bold border" [ngClass]="getPositionColor(player.position)">
                          {{ player.position }}
                        </span>
                        @for (position of player.secondaryPositions; track $index) {
                          <span class="px-1.5 py-0.2 rounded text-[10px] font-bold border" [ngClass]="getPositionColor(getPositionLabel(position))">
                            {{ getPositionLabel(position) }}
                          </span>
                        }
                      </span>
                    </div>
                    <div class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      <span>{{ player.age }} años</span>
                    </div>
                  </div>
                </div>

                <!-- Starter / Bench indicator -->
                <div>
                  @if (isPlayerStarter(player.id)) {
                    <span class="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                      <app-icon name="check" [size]="11" />
                      <span>Titular</span>
                    </span>
                  } @else if (isPlayerOnBench(player.id || player.playerInstanceId)) {
                    <span class="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border border-blue-300 dark:border-blue-800">
                      <span>Banquillo</span>
                    </span>
                  } @else {
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      Sin convocar
                    </span>
                  }
                </div>
              </div>

              <!-- Alerta de Lesión / Sanción -->
              @if (hasUnavailability(player)) {
                @let unav = getPrimaryUnavailability(player);
                <div
                  class="flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium border"
                  [ngClass]="getUnavailabilityBadgeClass(unav?.type)"
                  [title]="(unav?.typeLabel || getUnavailabilityTypeLabel(unav?.type)) + ': ' + (unav?.reasonLabel || getUnavailabilityReasonLabel(unav?.reason)) + (unav?.expectedEndDate ? ' (hasta ' + unav?.expectedEndDate + ')' : '')"
                >
                  <div class="flex items-center space-x-1.5 truncate">
                    <app-icon [name]="getUnavailabilityIcon(unav?.type)" [size]="13" class="shrink-0" />
                    <span class="font-bold uppercase text-[10px] tracking-wide">{{ unav?.typeLabel || getUnavailabilityTypeLabel(unav?.type) }}:</span>
                    <span class="truncate">{{ unav?.reasonLabel || getUnavailabilityReasonLabel(unav?.reason) }}</span>
                  </div>
                  @if (unav?.expectedEndDate) {
                    <span class="text-[10px] font-normal opacity-80 shrink-0 ml-1.5">hasta {{ unav?.expectedEndDate }}</span>
                  }
                </div>
              }

              <!-- 3 Metric Boxes: Valor de Mercado, Media Global, Media 3 Últimos -->
              <div class="grid grid-cols-3 gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/70 border border-slate-100 dark:border-slate-800/80 text-xs">
                <div>
                  <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Valor Mercado</div>
                  <div class="font-bold text-slate-900 dark:text-white font-mono text-sm mt-0.5">
                    {{ formatMarketValue(player.currentMarketValue) }}
                  </div>
                </div>

                <div>
                  <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Media Global</div>
                  <div class="font-bold text-amber-600 dark:text-amber-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                    <app-icon name="star" [size]="12" class="text-amber-500" />
                    <span>{{ formatPoints(player.averagePoints) }}</span>
                  </div>
                </div>

                <div>
                  <div class="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate">Media 3 Part.</div>
                  <div class="font-bold text-emerald-600 dark:text-emerald-400 font-mono text-sm mt-0.5 flex items-center space-x-1">
                    <app-icon name="trending-up" [size]="12" class="text-emerald-500" />
                    <span>{{ formatPoints(player.last3AveragePoints) }}</span>
                  </div>
                </div>
              </div>

              <!-- Card Actions -->
              <div class="flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800 text-xs">
                <div class="flex items-center space-x-1.5">
                  <!--@if (isPlayerStarter(player.id)) {
                    <button
                      type="button"
                      (click)="removePlayerFromLineup(player.id)"
                      class="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 transition-colors cursor-pointer inline-flex items-center gap-1"
                      title="Quitar del once titular"
                    >
                      <app-icon name="x" [size]="12" />
                      <span>Quitar</span>
                    </button>
                  } @else {
                    <button
                      type="button"
                      (click)="findSlotForPlayer(player)"
                      class="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 dark:hover:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-800/60 transition-colors cursor-pointer inline-flex items-center gap-1"
                      title="Alinear en el once titular"
                    >
                      <app-icon name="check" [size]="12" />
                      <span>Alinear</span>
                    </button>
                  }-->

                  @if (!isPlayerStarter(player.id)) {
                    <button
                      type="button"
                      (click)="toggleBench(player.id || player.playerInstanceId)"
                      class="px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer inline-flex items-center gap-1 border"
                      [ngClass]="isPlayerOnBench(player.id || player.playerInstanceId)
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200'"
                    >
                      <app-icon [name]="isPlayerOnBench(player.id || player.playerInstanceId) ? 'users' : 'plus'" [size]="12" />
                      <span>{{ isPlayerOnBench(player.id || player.playerInstanceId) ? 'Banquillo' : '+ Banquillo' }}</span>
                    </button>
                  }

                  @if (!player.isAmateur) {
                  <!--<button
                    type="button"
                    (click)="openPlayerDetail(player)"
                    class="px-2.5 py-1 rounded-lg text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    Ficha
                  </button>-->
                  <button
                    type="button"
                    (click)="openRenewalModal(player)"
                    class="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 dark:hover:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-800/60 transition-colors cursor-pointer inline-flex items-center gap-1"
                    title="Proponer renovación de contrato"
                  >
                    <app-icon name="file-text" [size]="11" />
                    <span>Contrato</span>
                  </button>
                  }
                </div>

                <div>
                  @if (!player.listedForTransfer) {
                    <button
                      (click)="openSellModal(player)"
                      class="px-2 py-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white rounded-lg text-[10px] font-medium transition-colors cursor-pointer"
                      title="Poner transferible en el mercado"
                     [disabled]="!game.canAction('sellPlayer')">
                      Transferible
                    </button>
                  } @else {
                    <button
                      [disabled]="game.transferPending().has(player.id)"
                      (click)="openWithdrawalModal(player)"
                      class="px-2 py-1 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/60 dark:hover:bg-rose-900/80 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60 rounded-lg text-[10px] font-bold transition-colors cursor-pointer"
                      title="Retirar de transferibles"
                    >
                      En venta ({{ player.askingPrice }}M)
                    </button>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }

      <!-- VISTA 2: TABLA REFORMULADA (Sin ficha ni cláusula; con escudo, valor, media global y media 3 últimos) -->
      @if (viewMode() === 'table') {
        <div class="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table class="w-full text-left text-xs">
            <thead class="bg-slate-50 dark:bg-slate-950/70 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th class="py-2.5 px-3 text-center">#</th>
                <th class="py-2.5 px-3">Futbolista</th>
                <th class="py-2.5 px-2 text-center">Pos</th>
                <th class="py-2.5 px-3 text-right">Valor Mercado</th>
                <th class="py-2.5 px-2 text-center">Media Global</th>
                <th class="py-2.5 px-2 text-center">Media 3 Últ.</th>
                <th class="py-2.5 px-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
              @for (player of players; track player.id) {
                <tr
                  class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                  [ngClass]="isPlayerStarter(player.id) ? 'bg-emerald-50/70 dark:bg-emerald-950/20' : (isPlayerOnBench(player.id || player.playerInstanceId) ? 'bg-blue-50/40 dark:bg-blue-950/20' : '')"
                >
                  <!-- Titular indicator -->
                  <td class="py-2.5 px-3 text-center">
                    @if (isPlayerStarter(player.id)) {
                      <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 font-bold text-[10px] border border-emerald-300 dark:border-emerald-500/30" title="Titular en el once">
                        11
                      </span>
                    } @else if (isPlayerOnBench(player.id || player.playerInstanceId)) {
                      <span class="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 font-bold text-[9px] border border-blue-300 dark:border-blue-500/30" title="Suplente en el banquillo">
                        SUP
                      </span>
                    } @else {
                      <span class="text-slate-400 dark:text-slate-600">-</span>
                    }
                  </td>

                  <!-- Futbolista + edad + alertas de baja -->
                  <td class="py-2.5 px-3">
                    <div class="flex items-start gap-2">
                    <div class="inline-flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-0.5 shadow-2xs">
                      @if (getPlayerCrestUrl(player); as crestUrl) {
                        <img
                          [src]="crestUrl"
                          [alt]="getPlayerClubShortName(player) || 'Club'"
                          (error)="onCrestError($event)"
                          referrerpolicy="no-referrer"
                          class="w-full h-full object-contain"
                          [title]="getPlayerClubName(player)"
                        />
                      } @else {
                        <span class="text-[8px] font-mono font-bold text-slate-500 uppercase">
                          {{ getPlayerClubShortName(player) || 'CLB' }}
                        </span>
                      }
                    </div>
                    <div class="min-w-0">
                    <div class="flex items-center gap-1.5 flex-wrap">
                      @if (player.isAmateur) {
                        <span class="font-bold text-slate-900 dark:text-white">{{ player.name }}</span>
                      } @else {
                      <button
                        type="button"
                        (click)="openPlayerDetail(player)"
                        class="group/name text-left font-bold text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors inline-flex items-center gap-1 cursor-pointer focus:outline-none"
                        title="Ver ficha técnica de {{ player.name }}"
                      >
                        <span class="underline decoration-transparent group-hover/name:decoration-emerald-500 underline-offset-2 transition-all">{{ player.name }}</span>
                        <app-icon name="arrow-up-right" [size]="11" class="opacity-0 group-hover/name:opacity-100 transition-opacity text-emerald-600 dark:text-emerald-400" />
                      </button>
                      }
                      <span class="text-slate-400 text-[11px]">({{ player.age }} años)</span>
                    </div>

                    <!-- Si está lesionado o sancionado -->
                    @if (hasUnavailability(player)) {
                      @let unav = getPrimaryUnavailability(player);
                      <div class="mt-1">
                        <span
                          class="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border"
                          [ngClass]="getUnavailabilityBadgeClass(unav?.type)"
                          [title]="(unav?.typeLabel || getUnavailabilityTypeLabel(unav?.type)) + ': ' + (unav?.reasonLabel || getUnavailabilityReasonLabel(unav?.reason)) + (unav?.expectedEndDate ? ' (hasta ' + unav?.expectedEndDate + ')' : '')"
                        >
                          <app-icon [name]="getUnavailabilityIcon(unav?.type)" [size]="11" class="shrink-0" />
                          <span>{{ unav?.typeLabel || getUnavailabilityTypeLabel(unav?.type) }}: {{ unav?.reasonLabel || getUnavailabilityReasonLabel(unav?.reason) }}</span>
                        </span>
                      </div>
                    }

                    @if (player.listedForTransfer) {
                      <span class="text-[10px] text-rose-600 dark:text-rose-400 block mt-0.5">Transferible ({{ player.askingPrice }} M€)</span>
                    }
                    </div>
                    </div>
                  </td>

                  <!-- Posición -->
                  <td class="py-2.5 px-2 text-center">
                    <div class="flex flex-col items-center gap-1">
                      <span class="px-2 py-0.5 rounded text-[10px] font-bold border" [ngClass]="getPositionColor(player.position)">
                        {{ player.position }}
                      </span>
                      @for (position of player.secondaryPositions; track $index) {
                        <span class="px-2 py-0.5 rounded text-[10px] font-bold border" [ngClass]="getPositionColor(getPositionLabel(position))">
                          {{ getPositionLabel(position) }}
                        </span>
                      }
                    </div>
                  </td>

                  <!-- Valor de Mercado -->
                  <td class="py-2.5 px-3 text-right font-mono font-bold text-slate-900 dark:text-white">
                    {{ formatMarketValue(player.currentMarketValue) }}
                  </td>

                  <!-- Media Global -->
                  <td class="py-2.5 px-2 text-center font-mono font-bold text-amber-600 dark:text-amber-400">
                    {{ formatPoints(player.averagePoints) }}
                  </td>

                  <!-- Media 3 Últimos Partidos -->
                  <td class="py-2.5 px-2 text-center font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    {{ formatPoints(player.last3AveragePoints) }}
                  </td>

                  <!-- Acciones -->
                  <td class="py-2.5 px-3 text-center">
                    <div class="flex items-center justify-center space-x-1">
                      @if (isPlayerStarter(player.id)) {
                        <button
                          type="button"
                          (click)="removePlayerFromLineup(player.id)"
                          class="p-1 rounded text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40 transition-colors cursor-pointer"
                          title="Quitar del once"
                        >
                          <app-icon name="x" [size]="13" />
                        </button>
                      } @else {
                        <button
                          type="button"
                          (click)="findSlotForPlayer(player)"
                          class="p-1 rounded text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40 transition-colors cursor-pointer"
                          title="Poner en el once titular"
                        >
                          <app-icon name="check" [size]="13" />
                        </button>
                      }

                      @if (!player.isAmateur) {
                        <button
                          type="button"
                          (click)="openRenewalModal(player)"
                          class="px-2 py-0.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded text-[10px] font-semibold transition-colors cursor-pointer border border-emerald-200 dark:border-emerald-800"
                          title="Proponer renovación"
                        >
                          Renovar
                        </button>
                      }

                      @if (!isPlayerStarter(player.id)) {
                        <button
                          type="button"
                          (click)="toggleBench(player.id || player.playerInstanceId)"
                          class="px-2 py-0.5 rounded text-[10px] font-semibold transition-colors cursor-pointer border"
                          [ngClass]="isPlayerOnBench(player.id || player.playerInstanceId)
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200'"
                          [title]="isPlayerOnBench(player.id || player.playerInstanceId) ? 'Quitar del banquillo' : 'Añadir al banquillo'"
                        >
                          {{ isPlayerOnBench(player.id || player.playerInstanceId) ? 'Banquillo' : '+ Banquillo' }}
                        </button>
                      }

                      @if (!player.listedForTransfer) {
                        <button
                          (click)="openSellModal(player)"
                          class="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-[10px] font-medium transition-colors cursor-pointer"
                          title="Poner transferible"
                         [disabled]="!game.canAction('sellPlayer')">
                          Venta
                        </button>
                      } @else {
                        <button
                          [disabled]="game.transferPending().has(player.id)"
                          (click)="openWithdrawalModal(player)"
                          class="px-2 py-0.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 rounded text-[10px] font-bold transition-colors cursor-pointer"
                          title="Retirar de venta"
                        >
                          Retirar
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
      </ng-template>
    </div>
  </div>

  <!-- Formation Selection Modal Dialog -->
  @if (isFormationModalOpen) {
    <div
      (click)="closeFormationModal()"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 cursor-pointer"
    >
      <div
        (click)="$event.stopPropagation()"
        class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl cursor-default"
      >
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
          <div class="flex items-center space-x-2.5">
            <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <app-icon name="shield" [size]="18" />
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-900 dark:text-white">
                {{ game.t('squad.formation') }}
              </h3>
              <p class="text-xs text-slate-500 dark:text-slate-400">Selecciona el esquema táctico para tu equipo</p>
            </div>
          </div>
          <button (click)="closeFormationModal()" class="text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <app-icon name="x" [size]="18" />
          </button>
        </div>

        <div class="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
          @for (f of formations; track f) {
            <div
              (click)="selectFormationFromModal(f)"
              class="flex items-center justify-between p-3.5 rounded-xl border transition-all cursor-pointer group"
              [ngClass]="game.activeFormation() === f 
                ? 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-500/60 ring-1 ring-emerald-500/30 shadow-xs' 
                : 'bg-slate-50 dark:bg-slate-950/60 border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 hover:bg-slate-100/80 dark:hover:bg-slate-900'"
            >
              <div class="flex items-center space-x-3.5">
                <!-- Formation code badge -->
                <div
                  class="w-14 h-11 rounded-xl flex items-center justify-center font-mono font-bold text-sm border shadow-xs transition-colors"
                  [ngClass]="game.activeFormation() === f 
                    ? 'bg-emerald-600 text-white border-emerald-500' 
                    : 'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 group-hover:border-emerald-500/40'"
                >
                  {{ f }}
                </div>

                <div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                      Esquema {{ f }}
                    </span>
                    @if (game.activeFormation() === f) {
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/30">
                        Activa
                      </span>
                    }
                  </div>
                  <!-- Breakdown badges: DEF / MED / DEL -->
                  <div class="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    <span class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60">
                      {{ getFormationBreakdown(f).def }} DEF
                    </span>
                    <span class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
                      {{ getFormationBreakdown(f).med }} MED
                    </span>
                    <span class="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60">
                      {{ getFormationBreakdown(f).del }} DEL
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex items-center space-x-2">
                @if (game.activeFormation() === f) {
                  <div class="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-xs">
                    <app-icon name="check" [size]="14" />
                  </div>
                } @else {
                  <div class="w-7 h-7 rounded-full border border-slate-300 dark:border-slate-700 group-hover:border-emerald-500 flex items-center justify-center text-transparent group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-all">
                    <app-icon name="chevron-right" [size]="14" />
                  </div>
                }
              </div>
            </div>
          }
        </div>

        <div class="pt-2 flex justify-end border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            (click)="closeFormationModal()"
            class="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Assign Player Modal (when clicking a pitch slot) -->
  @if (selectedSlotId) {
    <div
      (click)="selectedSlotId = null"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 cursor-pointer"
    >
      <div
        (click)="$event.stopPropagation()"
        class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl cursor-default"
      >
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h3 class="text-base font-bold text-slate-900 dark:text-white">
              Asignar a posición {{ selectedSlot()?.label }} ({{ selectedSlot()?.position }})
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">Elige un futbolista de la plantilla</p>
          </div>
          <button (click)="selectedSlotId = null" class="text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer">
            <app-icon name="x" [size]="18" />
          </button>
        </div>

        <div class="space-y-2 max-h-80 overflow-y-auto pr-1">
          @let candidates = candidatePlayers();
          @for (cand of candidates; track cand.id; let idx = $index) {
            <ng-container *ngTemplateOutlet="candidateRow; context: { cand: cand, idx: idx, candidates: candidates }" />
          }

          @let amateurs = candidateAmateurPlayers();
          @if (amateurs.length > 0) {
            <details class="group/amateurs mt-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
              <summary class="flex items-center justify-between gap-3 p-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-xs font-semibold text-slate-700 dark:text-slate-200">
                <span class="flex items-center gap-2">
                  <app-icon name="users" [size]="15" class="text-slate-400" />
                  <span>Amateurs</span>
                  <span class="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-[10px] font-mono">{{ amateurs.length }}</span>
                </span>
                <app-icon name="chevron-down" [size]="15" class="transition-transform group-open/amateurs:rotate-180" />
              </summary>
              <div class="p-2 pt-0 space-y-2">
                @for (cand of amateurs; track cand.id; let idx = $index) {
                  <ng-container *ngTemplateOutlet="candidateRow; context: { cand: cand, idx: idx, candidates: amateurs }" />
                }
              </div>
            </details>
          }
        </div>

        <ng-template #candidateRow let-cand="cand" let-idx="idx" let-candidates="candidates">
          @if (shouldShowOtherPositionsDivider(cand, idx, candidates)) {
            <div class="pt-2.5 pb-1 flex items-center gap-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
              <span class="h-px bg-slate-200 dark:bg-slate-800 flex-1"></span>
              <span>Otras posiciones</span>
              <span class="h-px bg-slate-200 dark:bg-slate-800 flex-1"></span>
            </div>
          }

          <div
            (click)="assignPlayer(cand.id)"
            class="flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer group"
            [ngClass]="cand.id === selectedSlot()?.playerId
              ? 'bg-sky-50/50 dark:bg-sky-950/30 border-sky-400 dark:border-sky-500 ring-1 ring-sky-500/20'
              : isPlayerStarter(cand.id)
              ? 'bg-emerald-50/20 dark:bg-emerald-950/20 border-emerald-500/40 hover:border-emerald-500'
              : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 hover:border-emerald-500/60'"
          >
            <div class="flex items-center space-x-3">
              <!-- Escudo Club Real -->
              <div class="relative w-7 h-7 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-0.5 shrink-0 shadow-2xs">
                @if (getPlayerCrestUrl(cand); as crestUrl) {
                  <img
                    [src]="crestUrl"
                    [alt]="getPlayerClubShortName(cand) || 'Club'"
                    (error)="onCrestError($event)"
                    referrerpolicy="no-referrer"
                    class="w-full h-full object-contain"
                    [title]="getPlayerClubName(cand)"
                  />
                } @else {
                  <span class="text-[8px] font-mono font-bold text-slate-500 uppercase">
                    {{ getPlayerClubShortName(cand) || 'CLB' }}
                  </span>
                }
              </div>

              <div>
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span class="px-1.5 py-0.2 rounded text-[10px] font-bold border" [ngClass]="getPositionColor(cand.position)">
                    {{ cand.position }}
                  </span>
                  <span class="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                    {{ cand.name }}
                  </span>

                  <!-- Indicador titular / banquillo -->
                  @if (cand.id === selectedSlot()?.playerId) {
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 dark:bg-sky-950/80 dark:text-sky-300 border border-sky-300 dark:border-sky-800 shadow-2xs">
                      <app-icon name="check" [size]="10" />
                      <span>Alineado aquí</span>
                    </span>
                  } @else if (isPlayerStarter(cand.id)) {
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 shadow-2xs">
                      <app-icon name="check" [size]="10" />
                      <span>Titular{{ getStarterSlotLabel(cand.id) ? ' (' + getStarterSlotLabel(cand.id) + ')' : '' }}</span>
                    </span>
                  } @else {
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200/70 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      Banquillo
                    </span>
                  }
                </div>

                <div class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <span>{{ getPlayerClubName(cand) }}</span>
                  <span>•</span>
                  <span>{{ cand.age }} años</span>
                  @if (cand.overall) {
                    <span>•</span>
                    <span class="font-semibold text-slate-700 dark:text-slate-300">{{ cand.overall }} OVR</span>
                  }
                </div>

                @if (hasUnavailability(cand)) {
                  @let unav = getPrimaryUnavailability(cand);
                  <div class="mt-1">
                    <span
                      class="inline-flex items-center space-x-1 px-1.5 py-0.2 rounded text-[10px] font-semibold border"
                      [ngClass]="getUnavailabilityBadgeClass(unav?.type)"
                    >
                      <app-icon [name]="getUnavailabilityIcon(unav?.type)" [size]="10" class="shrink-0" />
                      <span>{{ unav?.typeLabel || getUnavailabilityTypeLabel(unav?.type) }}: {{ unav?.reasonLabel || getUnavailabilityReasonLabel(unav?.reason) }}</span>
                    </span>
                  </div>
                }
              </div>
            </div>

            <!-- Métricas: Valor y Medias -->
            <div class="text-right flex flex-col items-end">
              <div class="text-xs font-bold text-slate-900 dark:text-white font-mono">
                {{ formatMarketValue(cand.currentMarketValue) }}
              </div>
              <div class="flex items-center space-x-1.5 mt-0.5 text-[11px] font-mono">
                <span class="text-amber-600 dark:text-amber-400 font-bold" title="Media global">
                  ★ {{ formatPoints(cand.averagePoints) }}
                </span>
                <span class="text-slate-300 dark:text-slate-600">|</span>
                <span class="text-emerald-600 dark:text-emerald-400 font-bold" title="Media 3 últimos">
                  📈 {{ formatPoints(cand.last3AveragePoints) }}
                </span>
              </div>
            </div>
          </div>
        </ng-template>

        <div class="pt-2 flex justify-between border-t border-slate-100 dark:border-slate-800">
          <button
            (click)="unassignCurrentSlot()"
            class="px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40 rounded-lg cursor-pointer font-medium"
          >
            Dejar posición vacía
          </button>
          <button
            (click)="selectedSlotId = null"
            class="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-lg cursor-pointer"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  }

  @if (withdrawingPlayer) {
    <div
      (click)="closeWithdrawalModal()"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="withdrawal-title"
        (click)="$event.stopPropagation()"
        class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-xl"
      >
        <h3 id="withdrawal-title" class="text-base font-bold text-slate-900 dark:text-white">Retirar de la venta</h3>
        <p class="text-sm text-slate-600 dark:text-slate-400">
          ¿Quieres retirar a <strong>{{ withdrawingPlayer.name }}</strong> de transferibles?
          Dejará de aparecer en el mercado.
        </p>
        <div class="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            (click)="closeWithdrawalModal()"
            [disabled]="game.transferPending().has(withdrawingPlayer.id)"
            class="px-3.5 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >Cancelar</button>
          <button
            type="button"
            (click)="confirmWithdrawal()"
            [disabled]="!game.canAction('sellPlayer') || (game.transferPending().has(withdrawingPlayer.id))"
            class="px-4 py-2 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-lg disabled:opacity-50"
          >{{ game.transferPending().has(withdrawingPlayer.id) ? 'Retirando…' : 'Retirar de la venta' }}</button>
        </div>
      </div>
    </div>
  }

  <!-- Transfer Listing Modal -->
  @if (sellingPlayer) {
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4"
    >
      <div
        (click)="$event.stopPropagation()"
        class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-xl cursor-default"
      >
        <h3 class="text-base font-bold text-slate-900 dark:text-white">Declarar Transferible</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Define el precio de salida para <strong>{{ sellingPlayer.name }}</strong> ({{ sellingPlayer.overall }} OVR).
        </p>

        <div>
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1" for="asking-price-input">
            Precio Solicitado (M€)
          </label>
          <input
            id="asking-price-input"
            type="text" inputmode="decimal" appMoneyInput
            step="0.5"
            min="0.5"
            [(ngModel)]="askingPriceInput"
            [disabled]="game.transferPending().has(sellingPlayer.id)"
            class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-mono text-emerald-600 dark:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>

        <div class="pt-3 flex justify-end space-x-2 border-t border-slate-100 dark:border-slate-800">
          <button
            [disabled]="game.transferPending().has(sellingPlayer.id)"
            (click)="sellingPlayer = null"
            class="px-3.5 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg cursor-pointer"
          >
            Cancelar
          </button>
          <button
            [disabled]="!game.canAction('sellPlayer') || (game.transferPending().has(sellingPlayer.id))"
            (click)="confirmListing()"
            class="px-4 py-2 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg cursor-pointer shadow-xs active:scale-98"
          >
            {{ game.transferPending().has(sellingPlayer.id) ? 'Publicando…' : 'Publicar en Mercado' }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ============================================== -->
  <!-- MODAL: RENOVAR CONTRATO                        -->
  <!-- ============================================== -->
  @if (showRenewalModal() && renewingPlayer(); as player) {
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs overflow-y-auto animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="renewal-modal-title"
    >
      <div class="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl p-5 sm:p-6 space-y-4 my-8">
        <!-- Modal Header -->
        <div class="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
          <div class="flex items-center space-x-3">
            @if (getPlayerCrestUrl(player); as crest) {
              <img [src]="crest" [alt]="getPlayerClubName(player)" class="w-10 h-10 object-contain shrink-0" (error)="onCrestError($event)" referrerpolicy="no-referrer" />
            }
            <div>
              <span class="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Contrato</span>
              <h3 id="renewal-modal-title" class="text-base font-bold text-slate-900 dark:text-white">
                {{ player.name }}
              </h3>
              <p class="text-xs text-slate-500">{{ player.position }} · {{ player.age }} años · Valor: {{ formatMarketValue(player.currentMarketValue) }}</p>
            </div>
          </div>

          <button
            type="button"
            (click)="closeRenewalModal()"
            id="btn-close-renewal-modal"
            class="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
            aria-label="Cerrar modal"
          >
            <app-icon name="x" [size]="18" />
          </button>
        </div>

        <!-- Information Notice
        <div class="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/60 rounded-xl text-blue-900 dark:text-blue-200 text-xs space-y-1">
          <div class="font-bold flex items-center gap-1.5">
            <app-icon name="info" [size]="14" />
            <span>Condiciones de Renovación</span>
          </div>
          <p class="leading-relaxed">
            La renovación no tiene coste de traspaso ni prima de fichaje. Queda <strong>pendiente del cierre diario</strong>.
            Si otros clubes presentan clausulazos, el futbolista comparará las propuestas y, <strong>en caso de empate, aceptará renovar</strong> permaneciendo en el club.
          </p>
        </div> -->

        <!-- Error Message Banner (inputs preserved on error) -->
        @if (renewalModalError()) {
          <div class="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-xl text-rose-800 dark:text-rose-300 text-xs flex items-center space-x-2">
            <app-icon name="alert-circle" [size]="16" class="shrink-0" />
            <span class="leading-relaxed">{{ renewalModalError() }}</span>
          </div>
        }

        <!-- Comparison Table: Current vs Proposed -->
        <div class="grid grid-cols-2 gap-3 text-xs">
          <!-- Current Contract -->
          <div class="p-3 rounded-xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-1.5">
            <div class="text-[10px] uppercase font-bold text-slate-400">Contrato Actual</div>
            <div class="space-y-1">
              <div class="flex justify-between">
                <span class="text-slate-500">Ficha:</span>
                <span class="font-mono font-bold text-slate-700 dark:text-slate-300">
                  {{ player.contract?.salary ? (player.contract.salary).toFixed(2) + ' M€' : '—' }}
                </span>
              </div>
              <!--<div class="flex justify-between">
                <span class="text-slate-500">Años:</span>
                <span class="font-mono font-bold text-slate-700 dark:text-slate-300">
                  {{ player.contract?.durationYears || 1 }} temp.
                </span>
              </div>-->
              <div class="flex justify-between">
                <span class="text-slate-500">Cláusula:</span>
                <span class="font-mono font-bold text-slate-700 dark:text-slate-300">
                  {{ player.contract?.releaseClause ? ((player.contract.releaseClause > 100000 ? player.contract.releaseClause / 1000000 : player.contract.releaseClause).toFixed(1) + ' M€') : '—' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Proposed Contract -->
          <div class="p-3 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 space-y-1.5">
            <div class="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400">Renovación</div>
            <div class="space-y-1">
              <div class="flex justify-between">
                <span class="text-slate-500">Ficha:</span>
                <span class="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                  {{ renewalAnnualSalaryM.toFixed(2) }} M€
                </span>
              </div>
              <!--<div class="flex justify-between">
                <span class="text-slate-500">Años:</span>
                <span class="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                  {{ renewalContractYears }} temp.
                </span>
              </div>-->
              <div class="flex justify-between">
                <span class="text-slate-500">Cláusula:</span>
                <span class="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                  {{ renewalReleaseClauseM.toFixed(1) }} M€
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Inputs -->
        <div class="space-y-3.5 text-xs">
          <!-- Ficha Anual -->
          <div>
            <div class="flex justify-between items-center mb-1">
              <label for="renewal-salary-input" class="font-semibold text-slate-700 dark:text-slate-300">
                Nueva Ficha Anual
              </label>
            </div>
            <div class="relative">
              <input
                id="renewal-salary-input"
                type="text" inputmode="decimal" appMoneyInput
                step="0.1"
                min="0.1"
                [(ngModel)]="renewalAnnualSalaryM"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-mono text-emerald-600 dark:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 pr-28"
              />
              <span class="absolute right-3.5 top-2.5 text-xs text-slate-400 font-mono pointer-events-none">
                {{ (renewalAnnualSalaryM * 1000000) | number:'1.0-0' }} €/año
              </span>
            </div>
          </div>

          <!-- Duración Contrato (Fija: 1 temporada) 
          <div>
            <div class="flex justify-between items-center mb-1">
              <span class="font-semibold text-slate-700 dark:text-slate-300">
                Duración del Nuevo Contrato
              </span>
              <span class="text-[10px] uppercase font-bold text-slate-400">Fijo</span>
            </div>
            <div class="px-3.5 py-2.5 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-medium text-slate-800 dark:text-slate-200 flex items-center justify-between">
              <span class="font-semibold">1 temporada</span>
              <span class="text-xs text-slate-400">Renovación anual</span>
            </div>
          </div>-->

          <!-- Nueva Cláusula -->
          <div>
            <div class="flex justify-between items-center mb-1">
              <label for="renewal-clause-input" class="font-semibold text-slate-700 dark:text-slate-300">
                Nueva Cláusula de Rescisión
              </label>
            </div>
            <div class="relative">
              <input
                id="renewal-clause-input"
                type="text" inputmode="decimal" appMoneyInput
                step="0.5"
                min="1"
                [(ngModel)]="renewalReleaseClauseM"
                class="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-mono text-emerald-600 dark:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 pr-28"
              />
              <span class="absolute right-3.5 top-2.5 text-xs text-slate-400 font-mono pointer-events-none">
                {{ (renewalReleaseClauseM * 1000000) | number:'1.0-0' }} €
              </span>
            </div>
          </div>

          <!-- Costes de la operación: 0 traspaso, 0 prima 
          <div class="p-2.5 bg-slate-100 dark:bg-slate-800/60 rounded-xl text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
            <span>Coste de traspaso / prima de fichaje:</span>
            <span class="font-mono font-bold text-slate-700 dark:text-slate-200">0 € (Renovación interna)</span>
          </div>-->
        </div>

        <!-- Modal Actions -->
        <div class="pt-3 flex justify-end space-x-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            (click)="closeRenewalModal()"
            class="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            id="btn-confirm-contract-renewal"
            (click)="confirmRenewal()"
            [disabled]="isSubmittingRenewal()"
            class="px-5 py-2 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl disabled:opacity-50 cursor-pointer shadow-xs flex items-center space-x-1.5 transition-all"
          >
            @if (isSubmittingRenewal()) {
              <div class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Enviando...</span>
            } @else {
              <app-icon name="check" [size]="15" />
              <span>Proponer Renovación</span>
            }
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Modal Selector de Suplentes para el Banquillo -->
  @if (isBenchPickerOpen()) {
    <div
      (click)="closeBenchPicker()"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 cursor-pointer"
    >
      <div
        (click)="$event.stopPropagation()"
        class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl cursor-default"
      >
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
          <div class="flex items-center space-x-2.5">
            <div class="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <app-icon name="users" [size]="18" />
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-900 dark:text-white">
                Añadir al Banquillo
              </h3>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Selecciona un jugador suplente ({{ benchCount() }}/5)
              </p>
            </div>
          </div>
          <button
            type="button"
            (click)="closeBenchPicker()"
            class="text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <app-icon name="x" [size]="18" />
          </button>
        </div>

        <div class="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          @if (availableBenchSubstitutes().length === 0) {
            <div class="p-6 text-center text-slate-400 text-xs">
              No hay jugadores disponibles fuera del once titular y del banquillo actual.
            </div>
          } @else {
            @for (player of availableBenchSubstitutes(); track player.id) {
              <div
                (click)="addBenchSubstitute(player.id || player.playerInstanceId)"
                class="flex items-center justify-between p-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-all cursor-pointer group"
              >
                <div class="flex items-center space-x-3">
                  <div class="w-9 h-9 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 flex items-center justify-center font-bold text-xs text-slate-600 dark:text-slate-300">
                    {{ player.name?.charAt(0) || '?' }}
                  </div>
                  <div>
                    <div class="text-xs font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                      {{ player.name }}
                    </div>
                    <div class="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
                      <span class="px-1 rounded bg-slate-100 dark:bg-slate-800 font-semibold">{{ player.position }}</span>
                      <span>{{ player.age }} años</span>
                      @if (player.isAmateur) {
                        <span class="text-amber-500 font-semibold">Amateur</span>
                      }
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    {{ player.averagePoints ?? 0 }} pts
                  </span>
                  <div class="w-6 h-6 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <app-icon name="plus" [size]="14" />
                  </div>
                </div>
              </div>
            }
          }
        </div>
      </div>
    </div>
  }

  @if (viewingPlayer) {
    <app-player-profile
      [playerDefinitionId]="viewingPlayer.playerDefinitionId || viewingPlayer.id"
      [playerName]="viewingPlayer.name"
      [dismissible]="false"
      (closed)="closePlayerDetail()"
    />
  }
</div>
